import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/LanguageContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { formatCurrency, formatDateTime } from "../lib/utils";
import USDHint from "../components/USDHint";
import PriceInput from "../components/PriceInput";
import toast from "react-hot-toast";
import axios from "axios";
import { Plus, Wallet, Trash2, FileText } from "lucide-react";
import { ReceiptDialog } from "../components/receipts/ReceiptDialog";

const API = `${window.location.origin}/api`;

const expenseTypes = [
  { value: "rent", labelAr: "إيجار", labelEn: "Rent" },
  { value: "utilities", labelAr: "كهرباء وماء", labelEn: "Utilities" },
  { value: "maintenance", labelAr: "صيانة", labelEn: "Maintenance" },
  { value: "transport", labelAr: "نقل", labelEn: "Transport" },
  { value: "supplies", labelAr: "مستلزمات", labelEn: "Supplies" },
  { value: "salaries", labelAr: "رواتب", labelEn: "Salaries" },
  { value: "marketing", labelAr: "تسويق", labelEn: "Marketing" },
  { value: "internet", labelAr: "إنترنت", labelEn: "Internet" },
  { value: "insurance", labelAr: "تأمين", labelEn: "Insurance" },
  { value: "taxes", labelAr: "ضرائب", labelEn: "Taxes" },
  { value: "other", labelAr: "أخرى", labelEn: "Other" },
];

export default function ExpensesPage() {
  const { } = useAuth();
  const language = "ar";
  const { t } = useTranslation(language);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [formData, setFormData] = useState({
    expense_type: "",
    custom_type: "",
    amount: "",
    payment_method: "cash",
    description: "",
  });

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${API}/expenses`);
      setExpenses(response.data);
    } catch (error) {
      toast.error(t("error"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const expenseType = formData.expense_type === "other" 
      ? formData.custom_type 
      : getExpenseTypeLabel(formData.expense_type);
    
    try {
      const response = await axios.post(`${API}/expenses`, {
        expense_type: expenseType,
        amount: parseFloat(formData.amount),
        payment_method: formData.payment_method,
        description: formData.description,
      });
      toast.success(language === "ar" ? "تم التسجيل بنجاح" : "Recorded successfully");
      
      // فتح الوصل
      setReceiptData({
        ...response.data,
        total: parseFloat(formData.amount),
        expense_type: expenseType,
      });
      setIsReceiptOpen(true);
      
      fetchExpenses();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === "ar" ? "هل أنت متأكد؟" : "Are you sure?")) return;
    try {
      await axios.delete(`${API}/expenses/${id}`);
      toast.success(language === "ar" ? "تم الحذف" : "Deleted");
      fetchExpenses();
    } catch (error) {
      toast.error(t("error"));
    }
  };

  const openReceipt = (expense) => {
    setReceiptData({
      ...expense,
      total: expense.amount,
    });
    setIsReceiptOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setFormData({ expense_type: "", custom_type: "", amount: "", payment_method: "cash", description: "" });
  };

  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  const getExpenseTypeLabel = (value) => {
    const type = expenseTypes.find(t => t.value === value);
    return type ? (language === "ar" ? type.labelAr : type.labelEn) : value;
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">{t("nav.expenses")}</h1>
          <p className="text-slate-400 mt-1">
            {language === "ar" ? "إجمالي المصاريف:" : "Total Expenses:"} {formatCurrency(totalExpenses)}
            <USDHint amount={totalExpenses} inline className="ml-2" />
          </p>
        </div>
        <Button
          onClick={() => setIsDialogOpen(true)}
          className="bg-red-600 hover:bg-red-700 btn-lift"
          data-testid="add-expense-btn"
        >
          <Plus className="w-4 h-4 me-2" />
          {t("addExpense")}
        </Button>
      </div>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-red-600/30 border-t-red-600 rounded-full animate-spin" />
            </div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-12">
              <Wallet className="w-16 h-16 mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400">{t("noData")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header border-slate-700">
                    <TableHead className="text-amber-500">{language === "ar" ? "رقم الوصل" : "Invoice #"}</TableHead>
                    <TableHead className="text-amber-500">{t("date")}</TableHead>
                    <TableHead className="text-amber-500">{t("expenseType")}</TableHead>
                    <TableHead className="text-amber-500">{t("amount")}</TableHead>
                    <TableHead className="text-amber-500">{t("description")}</TableHead>
                    <TableHead className="text-amber-500">{language === "ar" ? "الوصل" : "Receipt"}</TableHead>
                    <TableHead className="text-amber-500">{t("actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expenses.map((expense) => (
                    <TableRow key={expense.id} className="border-slate-700 hover:bg-slate-800/50">
                      <TableCell className="text-amber-400 font-mono font-bold">
                        {expense.invoice_number || "-"}
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm">
                        {formatDateTime(expense.created_at, language === "ar" ? "ar-IQ" : "en-US")}
                      </TableCell>
                      <TableCell className="text-white font-medium">
                        {expense.expense_type}
                      </TableCell>
                      <TableCell className="text-red-500 font-bold">
                        {formatCurrency(expense.amount)}
                      </TableCell>
                      <TableCell className="text-slate-300">{expense.description || "-"}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openReceipt(expense)}
                          className="text-slate-400 hover:text-amber-500"
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDelete(expense.id)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Expense Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">{t("addExpense")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-200">{t("expenseType")}</Label>
              <Select
                value={formData.expense_type}
                onValueChange={(value) => setFormData({ ...formData, expense_type: value })}
              >
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="expense-type-select">
                  <SelectValue placeholder={language === "ar" ? "اختر النوع" : "Select type"} />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {expenseTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value} className="text-white hover:bg-slate-700">
                      {language === "ar" ? type.labelAr : type.labelEn}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.expense_type === "other" && (
              <div className="space-y-2">
                <Label className="text-slate-200">{language === "ar" ? "نوع آخر" : "Other Type"}</Label>
                <Input
                  value={formData.custom_type}
                  onChange={(e) => setFormData({ ...formData, custom_type: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white"
                  placeholder={language === "ar" ? "أدخل النوع" : "Enter type"}
                  required
                />
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-slate-200">{t("amount")}</Label>
              <PriceInput
                value={parseFloat(formData.amount) || 0}
                onChange={(v) => setFormData({ ...formData, amount: String(v) })}
                placeholder="0"
                testId="expense-amount-input"
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-200">{t("description")}</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder={language === "ar" ? "تفاصيل إضافية..." : "Additional details..."}
                data-testid="expense-description-input"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="border-slate-600 text-slate-300">
                {t("cancel")}
              </Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="save-expense-btn">
                {t("save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      {receiptData && (
        <ReceiptDialog
          isOpen={isReceiptOpen}
          onClose={() => setIsReceiptOpen(false)}
          data={receiptData}
          type="expense"
        />
      )}
    </div>
  );
}
